import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useHeader from './useHeader';
import useDfcVillageDesktop, {
  DFC_VILLAGE_DESKTOP_MIN_WIDTH,
} from '../../../../hooks/useDfcVillageDesktop';
import { Company } from '../../../../hooks/useCompany';
import useDfcSurface from '../../../../hooks/useDfcSurface';
import { useAuth } from '../../../../context/AuthContext';
import { isValidUser } from '../../../../Utils/validate';
import useDfcAuthEntry from '../../../../hooks/useDfcAuthEntry';
import useDfcVillageNavItems, { isExternalNavTarget } from './useDfcVillageNavItems';

export type { DfcVillageNavItem } from './useDfcVillageNavItems';

/** Re-exported so the header variant's styles and tests keep one import path. */
export { DFC_VILLAGE_DESKTOP_MIN_WIDTH };

/**
 * Header state for the DFC Fitness Village theme.
 *
 * Extends the shared `useHeader` (cart, account, logo click, QR modal) with what the village
 * design adds: the surface, a top-level nav built from `company.sidebarContent` (the same
 * items the Sidebar shows, with the same link shape), and the sign-in entry shared with the
 * home and the menu (`useDfcAuthEntry`).
 */
const useDfcVillageHeader = (company?: Company) => {
  const header = useHeader(company);
  const surface = useDfcSurface();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { handleSignIn } = useDfcAuthEntry();
  const isDesktop = useDfcVillageDesktop();

  const isSignedIn = isValidUser(user);
  const navItems = useDfcVillageNavItems(company);

  const handleNavItemClick = useCallback(
    (to: string) => {
      if (isExternalNavTarget(to)) {
        window.open(to, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(to);
    },
    [navigate]
  );

  return {
    ...header,
    surface,
    isDesktop,
    isSignedIn,
    navItems,
    handleSignIn,
    handleNavItemClick,
    /**
     * The canvas draws the account entry and the menu on every village screen. Courts need an
     * account, so the theme always offers them rather than waiting on `hasAuthentication` /
     * `showSidebar` being configured (`Layout` mounts the drawer on the same terms).
     */
    showAccount: true,
    showMenuButton: true,
    /** The cart only earns a place in the bar once something is in it. */
    showCartButton: header.showCart && (header.bookingCart?.length ?? 0) > 0,
    cartItemsCount: header.bookingCart?.length ?? 0,
    enableQRCode: Boolean(company?.enableQRCode),
  };
};

export type DfcVillageHeaderState = ReturnType<typeof useDfcVillageHeader>;

export default useDfcVillageHeader;
